import React, { Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Box, Spinner, VStack, Text } from '@chakra-ui/react'

// Critical pages loaded eagerly for fast initial render
import { LoginPage } from './pages/LoginPage'
import { RegisterPage } from './pages/RegisterPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { HomePage } from './pages/HomePage'

// Core components needed for routing (kept eager)
import { ProtectedRoute, PublicRoute, RequireAuth, RequireProAccess, TokenBasedRoute } from './components/Auth/ProtectedRoute'
import { PostLoginRedirect } from './components/Auth/PostLoginRedirect'
import { AuthenticatedRouteTracker } from './components/Auth/AuthenticatedRouteTracker'
import { Toaster } from './components/ui/toaster'
import ViewAsBanner from './components/ui/ViewAsBanner'
import useAccountStatus from './hooks/useAccountStatus'
import ReadOnlyBanner from './components/Banner/ReadOnlyBanner'
import NetworkStatusBanner from './components/Banner/NetworkStatusBanner'
import { CookieConsentBanner } from './components/Legal/CookieConsentBanner'
import { MarketingGoogleAdsTracker } from './components/Legal/MarketingGoogleAdsTracker'
import { IntentPickerController } from './components/Onboarding/IntentPicker/IntentPickerController'
import { AnalyticsTracker } from './components/AnalyticsTracker'
import { useVersionCheck } from './hooks/useVersionCheck'
import { LightModeWrapper } from './components/Layout/LightModeWrapper'
import { PublicShareHelmet } from './components/Layout/PublicShareHelmet'
import { DashboardLayout } from './components/Dashboard/DashboardLayout'
import {
  PRACTITIONER_DIRECTORY_ENABLED,
  PRACTITIONER_SPOTLIGHT_ENABLED,
} from './config/practitionerFeatures'

import './styles/logical-properties.css'

const disabledRoute = (enabled: boolean, element: React.ReactElement) =>
  enabled ? element : <Navigate to="/" replace />

// Short-link alias for /plans-and-pricing; preserves the query string (e.g. ?source=).
const PlansRedirect: React.FC = () => {
  const location = useLocation()
  return <Navigate to={`/plans-and-pricing${location.search}`} replace />
}

// ============================================================================
// LAZY-LOADED PAGES
// These are loaded on-demand when the user navigates to them
// ============================================================================

// Auth & Account pages
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'))
const UnsubscribePage = React.lazy(() => import('./pages/UnsubscribePage').then(m => ({ default: m.UnsubscribePage })))
const ReminderManagementPage = React.lazy(() => import('./pages/ReminderManagementPage').then(m => ({ default: m.ReminderManagementPage })))
const QuickCheckinMagicPage = React.lazy(() => import('./pages/QuickCheckinMagicPage'))
const MigrationWaitPage = React.lazy(() => import('./pages/MigrationWaitPage').then(m => ({ default: m.MigrationWaitPage })))
const AccountPage = React.lazy(() => import('./pages/AccountPage'))
const TwoFactorSetupPage = React.lazy(() => import('./pages/TwoFactorSetupPage').then(m => ({ default: m.TwoFactorSetupPage })))

// Partner pages
const PhoenixPartnerPage = React.lazy(() => import('./pages/Partners/PhoenixPartnerPage').then(m => ({ default: m.PhoenixPartnerPage })))
const AffiliateProgramPage = React.lazy(() => import('./pages/Affiliate/AffiliateProgramPage').then(m => ({ default: m.AffiliateProgramPage })))

// Public pages
const PlansAndPricingPage = React.lazy(() => import('./pages/PlansAndPricingPage').then(m => ({ default: m.PlansAndPricingPage })))
const SendFeedbackPage = React.lazy(() => import('./pages/SendFeedbackPage').then(m => ({ default: m.SendFeedbackPage })))
const AboutPage = React.lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })))
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'))
const TermsArchivePage = React.lazy(() => import('./pages/TermsArchivePage'))
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'))
const GeneticConsentPage = React.lazy(() => import('./pages/GeneticConsentPage'))
const DNAKitTermsPage = React.lazy(() => import('./pages/DNAKitTermsPage'))
const PrivacyPolicyArchivePage = React.lazy(() => import('./pages/PrivacyPolicyArchivePage'))
const PartnerTermsPage = React.lazy(() => import('./pages/PartnerTermsPage'))
const ClientWaiverPage = React.lazy(() => import('./pages/ClientWaiverPage'))
const AccountabilityPartnerProgramPage = React.lazy(() => import('./pages/AccountabilityPartnerProgramPage'))
const AffiliateLandingPage = React.lazy(() => import('./pages/AffiliateLandingPage'))
const CoachingOffersPage = React.lazy(() => import('./pages/Coaching/HealthGoalsResetPage'))
const HealthGoalsResetDetailPage = React.lazy(() => import('./pages/Coaching/HealthGoalsResetDetailPage'))
const PersonalisedHealthCoachingPage = React.lazy(() => import('./pages/Coaching/PersonalisedHealthCoachingPage'))
const HealthGoalsResetApplicationPage = React.lazy(() => import('./pages/Coaching/HealthGoalsResetApplicationPage'))
const HealthGoalsResetAgreementPage = React.lazy(() => import('./pages/Coaching/HealthGoalsResetAgreementPage'))
const GoalsPartnerSprintPage = React.lazy(() => import('./pages/Programs/GoalsPartnerSprintPage'))
const AccountDeletionPage = React.lazy(() => import('./pages/AccountDeletionPage'))
const HowToUsePage = React.lazy(() => import('./pages/HowToUsePage'))
const PressPage = React.lazy(() => import('./pages/PressPage'))
const AiDocsPage = React.lazy(() => import('./pages/AiDocsPage'))
const DevelopersPage = React.lazy(() => import('./pages/DevelopersPage'))
const ClaudeConnectPage = React.lazy(() => import('./pages/ClaudeConnectPage'))
const PublicAccessGatePage = React.lazy(() => import('./pages/PublicAccessGatePage'))
const ScenariosSlugRoute = React.lazy(() =>
  import('./pages/ScenariosSlugRoute').then((m) => ({ default: m.ScenariosSlugRoute })),
)
const ScenarioPage = React.lazy(() => import('./pages/ScenarioPage'))
const ScenariosIndexPage = React.lazy(() => import('./pages/ScenariosIndexPage').then(m => ({ default: m.ScenariosIndexPage })))
const PresentationsIndexPage = React.lazy(() => import('./pages/PresentationsIndexPage').then(m => ({ default: m.PresentationsIndexPage })))
const PresentationDetailPage = React.lazy(() => import('./pages/PresentationDetailPage').then(m => ({ default: m.PresentationDetailPage })))
const TutorialsIndexPage = React.lazy(() => import('./pages/TutorialsIndexPage').then(m => ({ default: m.TutorialsIndexPage })))
const TutorialDetailPage = React.lazy(() => import('./pages/TutorialDetailPage').then(m => ({ default: m.TutorialDetailPage })))
const SpotlightLearnMorePage = React.lazy(() => import('./pages/SpotlightLearnMorePage'))
const SpotlightHowItWorksPage = React.lazy(() => import('./pages/SpotlightHowItWorksPage'))

// DNA Kit pages
const DNAKitPage = React.lazy(() => import('./pages/DNAKitPage').then(m => ({ default: m.DNAKitPage })))
const DNAKitGuestSuccessPage = React.lazy(() => import('./pages/DNAKitGuestSuccessPage').then(m => ({ default: m.DNAKitGuestSuccessPage })))
const DNAKitAuthSuccessPage = React.lazy(() => import('./pages/DNAKitAuthSuccessPage').then(m => ({ default: m.DNAKitAuthSuccessPage })))
const DNAKitRegistrationPage = React.lazy(() => import('./pages/DNAKitRegistrationPage').then(m => ({ default: m.DNAKitRegistrationPage })))
const CompleteKitShippingPage = React.lazy(() => import('./pages/CompleteKitShippingPage').then(m => ({ default: m.CompleteKitShippingPage })))
const ClaimKitPage = React.lazy(() => import('./pages/ClaimKitPage').then(m => ({ default: m.ClaimKitPage })))

// Profile pages
const AllProfilesPage = React.lazy(() => import('./pages/AllProfilesPage').then(m => ({ default: m.AllProfilesPage })))
const AddProfilePage = React.lazy(() => import('./pages/AddProfilePage').then(m => ({ default: m.AddProfilePage })))
const EditProfilePage = React.lazy(() => import('./pages/EditProfilePage').then(m => ({ default: m.EditProfilePage })))
const ViewProfilePage = React.lazy(() => import('./pages/ViewProfilePage').then(m => ({ default: m.ViewProfilePage })))
const ProfilePage = React.lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })))
const ShareProfilePage = React.lazy(() => import('./pages/ShareProfilePage'))
const PartnershipsPage = React.lazy(() => import('./pages/PartnershipsPage'))
const AcceptPartnershipPage = React.lazy(() => import('./pages/AcceptPartnershipPage'))
const PartnershipGoalsPage = React.lazy(() => import('./pages/PartnershipGoalsPage'))
const PartnershipFeedPage = React.lazy(() => import('./pages/PartnershipFeedPage'))
const PartnershipBillingPage = React.lazy(() => import('./pages/PartnershipBillingPage'))
const MyApProfilesPage = React.lazy(() => import('./pages/MyApProfilesPage'))

// Chat pages
const GeneChatPage = React.lazy(() => import('./pages/GeneChatPage'))
const LegacyGeneChatRedirect = React.lazy(() => import('./pages/LegacyGeneChatRedirect'))
const LegacyProfileGeneChatRedirect = React.lazy(() => import('./pages/LegacyProfileGeneChatRedirect'))

// Documents & Health Records
const DocumentsPage = React.lazy(() => import('./pages/DocumentsPage'))
const DocumentViewPage = React.lazy(() => import('./pages/DocumentViewPage'))
const HealthRecordsPage = React.lazy(() => import('./pages/HealthRecordsPage'))
const HealthRecordViewPage = React.lazy(() => import('./pages/HealthRecordViewPage'))

// Genetic Reports
const GenotypeReportsPage = React.lazy(() => import('./pages/GenotypeReportsPage'))
const SNPResultsPage = React.lazy(() => import('./pages/SNPResultsPage'))
const LegacyReportSectionsRedirect = React.lazy(() => import('./pages/LegacyReportSectionsRedirect'))
const LegacyReportIdRedirect = React.lazy(() => import('./pages/LegacyReportIdRedirect'))
const ReportSectionDetailPage = React.lazy(() => import('./pages/ReportSectionDetailPage'))
const ReportSummaryPage = React.lazy(() => import('./pages/ReportSummaryPage'))
const ReportInsightsPage = React.lazy(() => import('./pages/ReportInsightsPage'))
const CustomGeneticReportsListPage = React.lazy(() => import('./pages/CustomGeneticReportsListPage'))
const SNPTemplateEditorPage = React.lazy(() => import('./pages/SNPTemplateEditorPage'))
const SNPLibraryPage = React.lazy(() => import('./pages/SNPLibraryPage'))
const SNPTemplateDetailPage = React.lazy(() => import('./pages/SNPTemplateDetailPage'))

// Wellness pages
const WellnessGoalsPage = React.lazy(() => import('./pages/WellnessGoalsPage'))
const WellnessGoalDetailPage = React.lazy(() => import('./pages/WellnessGoalDetailPage'))
const WellnessGoalAnalyticsPage = React.lazy(() => import('./pages/WellnessGoalAnalyticsPage'))
const WellnessGoalCheckinPage = React.lazy(() => import('./pages/WellnessGoalCheckinPage'))
const LegacyGoalCreateRedirect = React.lazy(() => import('./pages/LegacyGoalCreateRedirect'))
const LegacyCoachRedirect = React.lazy(() => import('./pages/LegacyCoachRedirect'))
const WearablesPage = React.lazy(() => import('./pages/WearablesPage'))

// AI Connect OAuth consent page (must be public — user may arrive unauthenticated from Claude/ChatGPT)
const AIConnectPage = React.lazy(() => import('./pages/AIConnectPage'))

// Support & Messaging
const CustomerSupportPage = React.lazy(() => import('./pages/CustomerSupportPage'))
const LegacySupportConversationRedirect = React.lazy(() => import('./pages/LegacySupportConversationRedirect'))
const CollaboratorMessagingPage = React.lazy(() => import('./pages/CollaboratorMessagingPage').then(m => ({ default: m.CollaboratorMessagingPage })))

// Billing
const BillingCheckoutRedirect = React.lazy(() => import('./pages/BillingCheckoutRedirect'))
const BillingSuccessPage = React.lazy(() => import('./pages/BillingSuccessPage').then(m => ({ default: m.BillingSuccessPage })))

// Sharing & Invitations
const AcceptShareInvitePage = React.lazy(() => import('./pages/AcceptShareInvitePage').then(m => ({ default: m.AcceptShareInvitePage })))
const ClientOnboardingPage = React.lazy(() => import('./pages/ClientOnboardingPage').then(m => ({ default: m.ClientOnboardingPage })))
const ViewAsPage = React.lazy(() => import('./pages/ViewAsPage'))

// Admin pages (rarely accessed, good candidates for lazy loading)
const AdminUsersPage = React.lazy(() => import('./pages/AdminUsersPage'))
const AdminUserDetailPage = React.lazy(() => import('./pages/AdminUserDetailPage'))
const AdminActivityPage = React.lazy(() => import('./pages/AdminActivityPage'))
const AdminMcpAnalyticsPage = React.lazy(() => import('./pages/AdminMcpAnalyticsPage'))
const AdminLaunchMetricsPage = React.lazy(() => import('./pages/AdminLaunchMetricsPage'))
const AdminAccountStatsPage = React.lazy(() => import('./pages/AdminAccountStatsPage'))
const AdminMailingStatsPage = React.lazy(() => import('./pages/AdminMailingStatsPage'))
const AdminDiscoveryPage = React.lazy(() => import('./pages/Admin/AdminDiscoveryPage'))
const AdminDiscoverySurveyRespondentsPage = React.lazy(() => import('./pages/Admin/AdminDiscoverySurveyRespondentsPage'))
const AdminDiscoveryUserStatePage = React.lazy(() => import('./pages/Admin/AdminDiscoveryUserStatePage'))
const AdminPromotionsPage = React.lazy(() => import('./pages/AdminPromotionsPage'))
const AdminGoalsPartnerProfilesPage = React.lazy(() => import('./pages/Admin/AdminGoalsPartnerProfilesPage'))
const AdminCoachingApplicationsPage = React.lazy(() => import('./pages/Admin/AdminCoachingApplicationsPage'))
const AdminGoalsPartnerSprintPage = React.lazy(() => import('./pages/Admin/AdminGoalsPartnerSprintPage'))
const AdminAffiliatesPage = React.lazy(() => import('./pages/Admin/AdminAffiliatesPage'))
const AdminDNAKitsPage = React.lazy(() => import('./pages/AdminDNAKitsPage'))
const AdminSupportPage = React.lazy(() => import('./pages/AdminSupportPage'))
const AdminSupportConversationDetail = React.lazy(() => import('./components/Admin/AdminSupportConversationDetail'))
const AdminKnowledgeBasePage = React.lazy(() => import('./pages/Admin/AdminKnowledgeBasePage'))
const NewsletterAdminPage = React.lazy(() => import('./pages/Admin/NewsletterAdminPage'))
const VerificationReviewPage = React.lazy(() => import('./pages/Admin/VerificationReviewPage'))
const AdminBillingLayout = React.lazy(() => import('./pages/Admin/billing/AdminBillingLayout'))
const AdminBillingPlanGrantsPage = React.lazy(() => import('./pages/Admin/billing/AdminBillingPlanGrantsPage'))
const AdminBillingSubscriptionsPage = React.lazy(() => import('./pages/Admin/billing/AdminBillingSubscriptionsPage'))
const AdminBillingCouponsPage = React.lazy(() => import('./pages/Admin/billing/AdminBillingCouponsPage'))
const AdminAgentsPage = React.lazy(() => import('./pages/Admin/AdminAgentsPage'))
const AdminAgentDetailPage = React.lazy(() => import('./pages/Admin/agents/AdminAgentDetailPage'))
const AdminAgentRunDetailPage = React.lazy(() => import('./pages/Admin/agents/AdminAgentRunDetailPage'))
const AdminAgentFindingDetailPage = React.lazy(() => import('./pages/Admin/agents/AdminAgentFindingDetailPage'))

// Pro Dashboard pages
const ClientsPage = React.lazy(() => import('./pages/ProDashboard/ClientsPage').then(m => ({ default: m.ClientsPage })))
const TeamsPage = React.lazy(() => import('./pages/ProDashboard/TeamsPage').then(m => ({ default: m.TeamsPage })))
const ReportCardPage = React.lazy(() => import('./pages/ProDashboard/ReportCardPage').then(m => ({ default: m.ReportCardPage })))
const TemplatesPage = React.lazy(() => import('./pages/ProDashboard/TemplatesPage').then(m => ({ default: m.TemplatesPage })))
const AnalyticsPage = React.lazy(() => import('./pages/ProDashboard/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })))
const SettingsPage = React.lazy(() => import('./pages/ProDashboard/SettingsPage').then(m => ({ default: m.SettingsPage })))
const ProSupportPage = React.lazy(() => import('./pages/ProDashboard/ProSupportPage').then(m => ({ default: m.ProSupportPage })))
const FeedbackApprovalPage = React.lazy(() => import('./pages/ProDashboard/FeedbackApprovalPage').then(m => ({ default: m.FeedbackApprovalPage })))
const InquiriesPage = React.lazy(() => import('./pages/ProDashboard/InquiriesPage').then(m => ({ default: m.InquiriesPage })))

// Public pages
const PublicReportCardPage = React.lazy(() => import('./pages/PublicReportCardPage').then(m => ({ default: m.PublicReportCardPage })))
const PractitionerDirectoryPage = React.lazy(() => import('./pages/PractitionerDirectoryPage').then(m => ({ default: m.PractitionerDirectoryPage })))
const PractitionerDetailPage = React.lazy(() => import('./pages/PractitionerDetailPage').then(m => ({ default: m.PractitionerDetailPage })))
const PublicApProfilePage = React.lazy(() => import('./pages/PublicApProfilePage').then(m => ({ default: m.PublicApProfilePage })))

// Discovery pages
const DiscoveryLayout = React.lazy(() => import('./pages/Discovery/DiscoveryLayout'))
const DiscoveryFeedPage = React.lazy(() => import('./pages/Discovery/DiscoveryFeedPage'))
const DiscoveryResponsePage = React.lazy(() => import('./pages/Discovery/DiscoveryResponsePage'))
const DiscoveryResultsPage = React.lazy(() => import('./pages/Discovery/DiscoveryResultsPage'))
const DiscoveryEntryPage = React.lazy(() => import('./pages/Discovery/DiscoveryEntryPage'))
// Phase 2: Experiment outcome page deferred
// const ExperimentOutcomePage = React.lazy(() => import('./pages/Discovery/ExperimentOutcomePage'))

// Coach/Spotlight pages
const SpotlightFormPage = React.lazy(() => import('./pages/Coach/SpotlightFormPage').then(m => ({ default: m.SpotlightFormPage })))
const SpotlightFormPageDemo = React.lazy(() => import('./pages/Coach/SpotlightFormPageDemo').then(m => ({ default: m.SpotlightFormPageDemo })))
const SpotlightPage = React.lazy(() => import('./pages/SpotlightPage').then(m => ({ default: m.SpotlightPage })))
const SpotlightPageMockup = React.lazy(() => import('./pages/SpotlightPageMockup').then(m => ({ default: m.SpotlightPageMockup })))
const PaidPromotionPage = React.lazy(() => import('./pages/Coach/PaidPromotionPage').then(m => ({ default: m.PaidPromotionPage })))

// ============================================================================
// PAGE LOADING FALLBACK
// ============================================================================

function VersionChecker() {
  useVersionCheck()
  return null
}

const PageLoadingFallback = () => (
  <Box minH="50vh" display="flex" alignItems="center" justifyContent="center">
    <VStack gap={4}>
      <Spinner color="brand.500" size="lg" />
      <Text color="fg.muted" fontSize="sm">Loading...</Text>
    </VStack>
  </Box>
)

const ShellLoadingFallback = () => (
  <DashboardLayout>
    <Box minH="50vh" display="flex" alignItems="center" justifyContent="center">
      <VStack gap={4}>
        <Spinner color="brand.500" size="lg" />
        <Text color="fg.muted" fontSize="sm">Loading...</Text>
      </VStack>
    </Box>
  </DashboardLayout>
)

// ============================================================================
// APP COMPONENT
// ============================================================================

function App() {
  return <AppContent />
}

function AppContent() {
  const account = useAccountStatus()

  return (
    <Router>
      <AuthenticatedRouteTracker />
      <PublicShareHelmet />
      <VersionChecker />
      <AnalyticsTracker />
      {/* Read-only banner */}
      {account.readonly && <ReadOnlyBanner daysUntilDeletion={account.daysUntilDeletion} />}
      {/* Network status banner (shows when offline) */}
      <NetworkStatusBanner />
      {/* Suspense boundary for lazy-loaded pages */}
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          {/* Public landing page - EAGER LOADED */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <LightModeWrapper>
                  <HomePage />
                </LightModeWrapper>
              </PublicRoute>
            }
          />

          {/* Practitioner Spotlight learn-more page */}
          <Route
            path="/practitioner-spotlight"
            element={disabledRoute(
              PRACTITIONER_SPOTLIGHT_ENABLED,
              <LightModeWrapper>
                <SpotlightLearnMorePage />
              </LightModeWrapper>,
            )}
          />
          <Route
            path="/practitioner-spotlight/how-it-works"
            element={disabledRoute(
              PRACTITIONER_SPOTLIGHT_ENABLED,
              <LightModeWrapper>
                <SpotlightHowItWorksPage />
              </LightModeWrapper>,
            )}
          />

          {/* Partner landing pages */}
          <Route path="/partners/phoenix" element={<LightModeWrapper><PhoenixPartnerPage /></LightModeWrapper>} />

          {/* Plans & pricing page - accessible to all, do not redirect authenticated users */}
          <Route path="/plans-and-pricing" element={<PlansAndPricingPage />} />
          {/* Short alias - preserve query string (e.g. ?source=) */}
          <Route path="/plans" element={<PlansRedirect />} />

          <Route path="/about" element={<LightModeWrapper><AboutPage /></LightModeWrapper>} />

          {/* DNA Kit page - accessible to all */}
          <Route path="/dna-kit" element={<LightModeWrapper><DNAKitPage /></LightModeWrapper>} />

          {/* DNA Kit guest purchase success page */}
          <Route path="/dna-kit-success" element={<LightModeWrapper><DNAKitGuestSuccessPage /></LightModeWrapper>} />
          <Route path="/dna-kit-guest-success" element={<LightModeWrapper><DNAKitGuestSuccessPage /></LightModeWrapper>} />

          {/* DNA Kit authenticated user purchase success page */}
          <Route path="/dna-kit-auth-success" element={<RequireAuth><DNAKitAuthSuccessPage /></RequireAuth>} />

          {/* DNA Kit shipping-completion magic link (no login required) */}
          <Route path="/dna-kit/shipping" element={<LightModeWrapper><CompleteKitShippingPage /></LightModeWrapper>} />
          <Route path="/dna-kit/claim" element={<TokenBasedRoute><LightModeWrapper><ClaimKitPage /></LightModeWrapper></TokenBasedRoute>} />

          {/* Terms of Service page */}
          <Route path="/terms" element={<LightModeWrapper><TermsOfServicePage /></LightModeWrapper>} />
          <Route path="/terms/archive" element={<LightModeWrapper><TermsArchivePage /></LightModeWrapper>} />

          {/* Privacy Policy page */}
          <Route path="/privacy" element={<LightModeWrapper><PrivacyPolicyPage /></LightModeWrapper>} />
          <Route path="/privacy/archive" element={<LightModeWrapper><PrivacyPolicyArchivePage /></LightModeWrapper>} />
          <Route path="/genetic-consent" element={<LightModeWrapper><GeneticConsentPage /></LightModeWrapper>} />
          <Route path="/dna-kit-terms" element={<LightModeWrapper><DNAKitTermsPage /></LightModeWrapper>} />

          {/* Accountability Partner terms & waiver pages */}
          <Route path="/partner-terms" element={<LightModeWrapper><PartnerTermsPage /></LightModeWrapper>} />
          <Route path="/client-waiver" element={<LightModeWrapper><ClientWaiverPage /></LightModeWrapper>} />

          {/* Goals Partners Program public page */}
          <Route path="/goals-partners" element={<LightModeWrapper><AccountabilityPartnerProgramPage /></LightModeWrapper>} />
          <Route path="/accountability-partners" element={<Navigate to="/goals-partners" replace />} />
          <Route path="/affiliates" element={<LightModeWrapper><AffiliateLandingPage /></LightModeWrapper>} />
          <Route path="/coaching" element={<LightModeWrapper><CoachingOffersPage /></LightModeWrapper>} />
          <Route path="/coaching/health-goals-reset" element={<LightModeWrapper><HealthGoalsResetDetailPage /></LightModeWrapper>} />
          <Route path="/coaching/health-goals-reset/reset" element={<Navigate to="/coaching/health-goals-reset" replace />} />
          <Route path="/coaching/personalised-health-coaching" element={<LightModeWrapper><PersonalisedHealthCoachingPage /></LightModeWrapper>} />
          <Route path="/coaching/apply" element={<LightModeWrapper><HealthGoalsResetApplicationPage /></LightModeWrapper>} />
          <Route path="/coaching/health-goals-reset/apply" element={<LightModeWrapper><HealthGoalsResetApplicationPage /></LightModeWrapper>} />
          <Route path="/coaching/health-goals-reset/agreement" element={<LightModeWrapper><HealthGoalsResetAgreementPage /></LightModeWrapper>} />
          <Route path="/goals-partner-sprint" element={<LightModeWrapper><GoalsPartnerSprintPage /></LightModeWrapper>} />

          {/* Press & media page */}
          <Route path="/press" element={<LightModeWrapper><PressPage /></LightModeWrapper>} />

          {/* Account deletion page (required by Google Play and Apple App Store) */}
          <Route path="/account/delete" element={<LightModeWrapper><AccountDeletionPage /></LightModeWrapper>} />

          {/* How to Use LiveWello guide pages */}
          <Route path="/docs/ai" element={<LightModeWrapper><AiDocsPage /></LightModeWrapper>} />
          <Route path="/developers" element={<LightModeWrapper><DevelopersPage /></LightModeWrapper>} />
          <Route path="/claude" element={<LightModeWrapper><ClaudeConnectPage /></LightModeWrapper>} />
          <Route path="/how-to-use" element={<LightModeWrapper><HowToUsePage /></LightModeWrapper>} />
          <Route path="/how-to-use/:category" element={<LightModeWrapper><HowToUsePage /></LightModeWrapper>} />
          <Route path="/how-to-use/:category/:useCaseId" element={<LightModeWrapper><HowToUsePage /></LightModeWrapper>} />

          {/* Feedback page - requires authentication */}
          <Route path="/feedback" element={<RequireAuth><SendFeedbackPage /></RequireAuth>} />

          {/* Public emergency access gate */}
          <Route path="/me/:profileUsername" element={<PublicRoute><PublicAccessGatePage /></PublicRoute>} />

          {/* Auth pages - EAGER LOADED for fast auth flow */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
          
          {/* AI Connect OAuth consent page — accessible regardless of auth state */}
          <Route path="/ai-connect" element={<TokenBasedRoute><AIConnectPage /></TokenBasedRoute>} />

          {/* Email management pages (token-based, accessible to both authenticated and unauthenticated users) */}
          <Route path="/unsubscribe" element={<TokenBasedRoute><UnsubscribePage /></TokenBasedRoute>} />
          <Route path="/reminders/manage" element={<TokenBasedRoute><ReminderManagementPage /></TokenBasedRoute>} />
          <Route path="/checkin/quick" element={<Suspense fallback={<ShellLoadingFallback />}><QuickCheckinMagicPage /></Suspense>} />
          
          {/* Migration wait page (shown after password reset for legacy users) */}
          <Route path="/migration-wait" element={<RequireAuth><MigrationWaitPage /></RequireAuth>} />
          
          {/* Post-login redirect to default profile */}
          <Route path="/redirect-to-profile" element={<RequireAuth><PostLoginRedirect /></RequireAuth>} />
          {/* Billing checkout redirector */}
          <Route path="/billing/checkout" element={<RequireAuth><BillingCheckoutRedirect /></RequireAuth>} />
          {/* Billing success page */}
          <Route path="/billing/success" element={<RequireAuth><BillingSuccessPage /></RequireAuth>} />

          {/* Protected account pages */}
          <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
          <Route path="/account/security/2fa-setup" element={<RequireAuth><TwoFactorSetupPage /></RequireAuth>} />
          <Route path="/dna-kit/register" element={<RequireAuth><DNAKitRegistrationPage /></RequireAuth>} />
          <Route path="/account/profiles" element={<RequireAuth><AllProfilesPage /></RequireAuth>} />
          <Route path="/account/sharing" element={<RequireAuth><AccountPage /></RequireAuth>} />
          <Route path="/account/affiliate" element={<RequireAuth><AffiliateProgramPage /></RequireAuth>} />
          <Route path="/support" element={<RequireAuth><CustomerSupportPage /></RequireAuth>} />
          <Route path="/support/conversations/:conversationId" element={<RequireAuth><LegacySupportConversationRedirect /></RequireAuth>} />
          <Route path="/support/conversation/:conversationId" element={<RequireAuth><CustomerSupportPage /></RequireAuth>} />
          <Route path="/support/ticket/:ticketId" element={<RequireAuth><CustomerSupportPage /></RequireAuth>} />
          <Route path="/snp-library" element={<RequireAuth><SNPLibraryPage /></RequireAuth>} />
          <Route path="/snp-library/template/:templateId" element={<RequireAuth><SNPTemplateDetailPage /></RequireAuth>} />

          {/* Admin routes */}
          <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
          <Route path="/admin/users" element={<ProtectedRoute requireAuth requireAdmin><AdminUsersPage /></ProtectedRoute>} />
          <Route path="/admin/users/:uid" element={<ProtectedRoute requireAuth requireAdmin><AdminUserDetailPage /></ProtectedRoute>} />
          <Route
            path="/admin/billing"
            element={
              <ProtectedRoute requireAuth requireAdmin>
                <AdminBillingLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="plan-grants" replace />} />
            <Route path="plan-grants" element={<AdminBillingPlanGrantsPage />} />
            <Route path="subscriptions" element={<AdminBillingSubscriptionsPage />} />
            <Route path="coupons" element={<AdminBillingCouponsPage />} />
          </Route>
          <Route path="/admin/dna-kits" element={<ProtectedRoute requireAuth requireAdmin><AdminDNAKitsPage /></ProtectedRoute>} />
          <Route path="/admin/promotions" element={<ProtectedRoute requireAuth requireAdmin><AdminPromotionsPage /></ProtectedRoute>} />
          <Route path="/admin/goals-partner-profiles" element={<ProtectedRoute requireAuth requireAdmin><AdminGoalsPartnerProfilesPage /></ProtectedRoute>} />
          <Route path="/admin/coaching-applications" element={<ProtectedRoute requireAuth requireAdmin><AdminCoachingApplicationsPage /></ProtectedRoute>} />
          <Route path="/admin/goals-partner-sprint" element={<ProtectedRoute requireAuth requireAdmin><AdminGoalsPartnerSprintPage /></ProtectedRoute>} />
          <Route path="/admin/affiliates" element={<ProtectedRoute requireAuth requireAdmin><AdminAffiliatesPage /></ProtectedRoute>} />
          <Route path="/admin/activity" element={<ProtectedRoute requireAuth requireAdmin><AdminActivityPage /></ProtectedRoute>} />
          <Route path="/admin/mcp" element={<ProtectedRoute requireAuth requireAdmin><AdminMcpAnalyticsPage /></ProtectedRoute>} />
          <Route path="/admin/launch-metrics" element={<ProtectedRoute requireAuth requireAdmin><AdminLaunchMetricsPage /></ProtectedRoute>} />
          <Route path="/admin/account-stats" element={<ProtectedRoute requireAuth requireAdmin><AdminAccountStatsPage /></ProtectedRoute>} />
          <Route path="/admin/mailing-stats" element={<ProtectedRoute requireAuth requireAdmin><AdminMailingStatsPage /></ProtectedRoute>} />
          <Route path="/admin/discovery" element={<ProtectedRoute requireAuth requireAdmin><AdminDiscoveryPage /></ProtectedRoute>} />
          <Route path="/admin/discovery/surveys/:surveyId/respondents" element={<ProtectedRoute requireAuth requireAdmin><AdminDiscoverySurveyRespondentsPage /></ProtectedRoute>} />
          <Route path="/admin/discovery/users/:userId" element={<ProtectedRoute requireAuth requireAdmin><AdminDiscoveryUserStatePage /></ProtectedRoute>} />
          <Route path="/admin/newsletter" element={<ProtectedRoute requireAuth requireAdmin><NewsletterAdminPage /></ProtectedRoute>} />
          <Route path="/admin/agents/runs/:runId" element={<ProtectedRoute requireAuth requireAdmin><AdminAgentRunDetailPage /></ProtectedRoute>} />
          <Route path="/admin/agents/findings/:findingId" element={<ProtectedRoute requireAuth requireAdmin><AdminAgentFindingDetailPage /></ProtectedRoute>} />
          <Route path="/admin/agents/:agentId" element={<ProtectedRoute requireAuth requireAdmin><AdminAgentDetailPage /></ProtectedRoute>} />
          <Route path="/admin/agents" element={<ProtectedRoute requireAuth requireAdmin><AdminAgentsPage /></ProtectedRoute>} />
          <Route path="/admin/verification" element={<ProtectedRoute requireAuth requireAdmin><VerificationReviewPage /></ProtectedRoute>} />
          <Route path="/admin/support" element={<ProtectedRoute requireAuth requireAdmin><AdminSupportPage /></ProtectedRoute>} />
          <Route path="/admin/support/knowledge-base" element={<ProtectedRoute requireAuth requireAdmin><AdminKnowledgeBasePage /></ProtectedRoute>} />
          <Route path="/admin/support/:conversationId" element={<ProtectedRoute requireAuth requireAdmin><AdminSupportConversationDetail /></ProtectedRoute>} />
          <Route path="/admin/knowledge-base" element={<Navigate to="/admin/support/knowledge-base" replace />} />

          {/* View-as route (token-based, handles its own auth checks) */}
          <Route path="/view-as" element={<TokenBasedRoute><ViewAsPage /></TokenBasedRoute>} />

          {/* Protected profile pages */}
          <Route path="/profiles" element={<RequireAuth><AllProfilesPage /></RequireAuth>} />
          <Route path="/profiles/add" element={<RequireAuth><AddProfilePage /></RequireAuth>} />
          <Route path="/profiles/edit/:profileId" element={<RequireAuth><EditProfilePage /></RequireAuth>} />
          <Route path="/profiles/view/:profileId" element={<RequireAuth><ViewProfilePage /></RequireAuth>} />
          <Route path="/profiles/share/:profileId" element={<RequireAuth><ShareProfilePage /></RequireAuth>} />

          {/* Accountability Partner (Phase 1 lifecycle UI + Phase 2 goals dashboard + Phase 2b interactions feed) */}
          <Route path="/partnerships" element={<RequireAuth><PartnershipsPage /></RequireAuth>} />
          <Route path="/partnerships/:partnershipId/accept" element={<RequireAuth><AcceptPartnershipPage /></RequireAuth>} />
          <Route path="/partnerships/:partnershipId/goals" element={<RequireAuth><Suspense fallback={<ShellLoadingFallback />}><PartnershipGoalsPage /></Suspense></RequireAuth>} />
          <Route path="/partnerships/:partnershipId/feed" element={<RequireAuth><Suspense fallback={<ShellLoadingFallback />}><PartnershipFeedPage /></Suspense></RequireAuth>} />
          <Route path="/partnerships/:partnershipId/billing" element={<RequireAuth><Suspense fallback={<ShellLoadingFallback />}><PartnershipBillingPage /></Suspense></RequireAuth>} />

          {/* Accountability Partner own-listing management (Phase 4) — no
              browsable/searchable directory; partners share their /ap/:username
              link directly (product decision: an open roster is a liability). */}
          <Route path="/partners/my-listing" element={<RequireAuth><Suspense fallback={<ShellLoadingFallback />}><MyApProfilesPage /></Suspense></RequireAuth>} />

          {/* Public Accountability Partner profile — no auth required, reachable
              by signed-in or signed-out visitors alike (see TokenBasedRoute). */}
          <Route path="/ap/:username" element={<TokenBasedRoute><Suspense fallback={<ShellLoadingFallback />}><PublicApProfilePage /></Suspense></TokenBasedRoute>} />

          {/* Simplified routes using ActiveProfileContext */}
          <Route path="/dashboard" element={<RequireAuth><ProfilePage /></RequireAuth>} />
          <Route path="/chat" element={<RequireAuth><GeneChatPage /></RequireAuth>} />
          {/* Coaching sessions are retired: old links open the Coach (see LegacyCoachRedirect) */}
          <Route path="/chat/coaching" element={<RequireAuth><LegacyCoachRedirect /></RequireAuth>} />
          <Route path="/chat/coaching/:threadId" element={<RequireAuth><LegacyCoachRedirect /></RequireAuth>} />
          <Route path="/chat/:threadId" element={<RequireAuth><GeneChatPage /></RequireAuth>} />
          {/* Legacy GeneChat aliases */}
          <Route path="/genechat" element={<RequireAuth><LegacyGeneChatRedirect /></RequireAuth>} />
          <Route path="/genechat/:threadId" element={<RequireAuth><LegacyGeneChatRedirect /></RequireAuth>} />
          <Route path="/profile/:profileId/genechat" element={<RequireAuth><LegacyProfileGeneChatRedirect /></RequireAuth>} />
          <Route path="/profile/:profileId/genechat/:threadId" element={<RequireAuth><LegacyProfileGeneChatRedirect /></RequireAuth>} />
          <Route path="/genetic-reports" element={<RequireAuth><GenotypeReportsPage /></RequireAuth>} />
          <Route path="/genetic-reports/snps" element={<RequireAuth><SNPResultsPage /></RequireAuth>} />
          <Route path="/genetic-reports/comprehensive" element={<RequireAuth><LegacyReportSectionsRedirect reportType="comprehensive" /></RequireAuth>} />
          <Route path="/genetic-reports/comprehensive/section/:sectionId" element={<RequireAuth><ReportSectionDetailPage reportType="comprehensive" /></RequireAuth>} />
          <Route path="/genetic-reports/comprehensive/summary" element={<RequireAuth><ReportSummaryPage reportType="comprehensive" /></RequireAuth>} />
          <Route path="/genetic-reports/comprehensive/insights" element={<RequireAuth><ReportInsightsPage reportType="comprehensive" /></RequireAuth>} />
          <Route path="/genetic-reports/comprehensive/insights/:sectionId" element={<RequireAuth><ReportInsightsPage reportType="comprehensive" /></RequireAuth>} />
          <Route path="/genetic-reports/custom" element={<RequireAuth><LegacyReportSectionsRedirect reportType="custom" /></RequireAuth>} />
          <Route path="/genetic-reports/custom/section/:sectionId" element={<RequireAuth><ReportSectionDetailPage reportType="custom" /></RequireAuth>} />
          <Route path="/genetic-reports/custom/summary" element={<RequireAuth><ReportSummaryPage reportType="custom" /></RequireAuth>} />
          <Route path="/genetic-reports/custom/insights" element={<RequireAuth><ReportInsightsPage reportType="custom" /></RequireAuth>} />
          <Route path="/genetic-reports/custom/insights/:sectionId" element={<RequireAuth><ReportInsightsPage reportType="custom" /></RequireAuth>} />
          <Route path="/genetic-reports/custom-reports" element={<RequireAuth><CustomGeneticReportsListPage /></RequireAuth>} />
          <Route path="/genetic-reports/:reportId" element={<RequireAuth><LegacyReportIdRedirect /></RequireAuth>} />
          <Route path="/genetic-reports/snp-template-editor" element={<RequireAuth><SNPTemplateEditorPage /></RequireAuth>} />
          <Route path="/wellness-goals" element={<RequireAuth><Suspense fallback={<ShellLoadingFallback />}><WellnessGoalsPage /></Suspense></RequireAuth>} />
          <Route path="/wellness-goals/create" element={<RequireAuth><Suspense fallback={<ShellLoadingFallback />}><LegacyGoalCreateRedirect /></Suspense></RequireAuth>} />
          <Route path="/wellness-goals/:goalId" element={<RequireAuth><Suspense fallback={<ShellLoadingFallback />}><WellnessGoalDetailPage /></Suspense></RequireAuth>} />
          <Route path="/wellness-goals/:goalId/analytics" element={<RequireAuth><Suspense fallback={<ShellLoadingFallback />}><WellnessGoalAnalyticsPage /></Suspense></RequireAuth>} />
          <Route path="/wellness-goals/:goalId/checkin" element={<RequireAuth><Suspense fallback={<ShellLoadingFallback />}><WellnessGoalCheckinPage /></Suspense></RequireAuth>} />
          {/* Legacy Wellness Coach aliases — old coaching-session links open the Coach */}
          <Route path="/profile/:profileId/wellness-coach" element={<RequireAuth><LegacyCoachRedirect /></RequireAuth>} />
          <Route path="/wellness-coach" element={<RequireAuth><LegacyCoachRedirect /></RequireAuth>} />
          <Route path="/wellness-coach/:threadId" element={<RequireAuth><LegacyCoachRedirect /></RequireAuth>} />
          {/* Unified Collaborator Messaging */}
          <Route path="/messages" element={<RequireAuth><CollaboratorMessagingPage /></RequireAuth>} />
          <Route path="/messages/:conversationId" element={<RequireAuth><CollaboratorMessagingPage /></RequireAuth>} />
          {/* Legacy routes - redirect to new unified messaging */}
          <Route path="/coach-messages" element={<RequireAuth><CollaboratorMessagingPage /></RequireAuth>} />
          <Route path="/coach-messages/:conversationId" element={<RequireAuth><CollaboratorMessagingPage /></RequireAuth>} />
          <Route path="/documents" element={<RequireAuth><DocumentsPage /></RequireAuth>} />
          <Route path="/documents/:documentId" element={<RequireAuth><DocumentViewPage /></RequireAuth>} />
          <Route path="/health-records" element={<RequireAuth><HealthRecordsPage /></RequireAuth>} />
          <Route path="/health-records/category/:category" element={<RequireAuth><HealthRecordsPage /></RequireAuth>} />
          <Route path="/health-records/record/:recordId" element={<RequireAuth><HealthRecordViewPage /></RequireAuth>} />
          <Route path="/wearables" element={<RequireAuth><WearablesPage /></RequireAuth>} />

          {/* Unified invite/share acceptance (supports both URL patterns) */}
          <Route path="/accept-invite/:token" element={<RequireAuth><AcceptShareInvitePage /></RequireAuth>} />
          <Route path="/accept-share" element={<RequireAuth><AcceptShareInvitePage /></RequireAuth>} />
          
          {/* Client onboarding flow (after accepting a Pro user's invitation) */}
          <Route path="/client-onboarding" element={<RequireAuth><ClientOnboardingPage /></RequireAuth>} />

          {/* Pro Dashboard */}
          <Route path="/pro/dashboard" element={<RequireProAccess><Navigate to="/pro/clients" replace /></RequireProAccess>} />
          <Route path="/pro/clients" element={<RequireProAccess><ClientsPage /></RequireProAccess>} />
          <Route path="/pro/teams" element={<RequireProAccess><TeamsPage /></RequireProAccess>} />
          <Route path="/pro/report-card" element={<RequireProAccess><ReportCardPage /></RequireProAccess>} />
          <Route path="/pro/templates" element={<RequireProAccess><TemplatesPage /></RequireProAccess>} />
          <Route path="/pro/feedback" element={<RequireProAccess><FeedbackApprovalPage /></RequireProAccess>} />
          <Route path="/pro/inquiries" element={disabledRoute(PRACTITIONER_SPOTLIGHT_ENABLED, <RequireProAccess><InquiriesPage /></RequireProAccess>)} />
          {/* Legacy pro messages - redirect to unified messaging */}
          <Route path="/pro/messages" element={<RequireProAccess><CollaboratorMessagingPage /></RequireProAccess>} />
          <Route path="/pro/messages/:conversationId" element={<RequireProAccess><CollaboratorMessagingPage /></RequireProAccess>} />
          <Route path="/pro/analytics" element={<RequireProAccess><AnalyticsPage /></RequireProAccess>} />
          <Route path="/pro/marketing" element={<RequireProAccess><PaidPromotionPage /></RequireProAccess>} />
          <Route path="/pro/settings" element={<RequireProAccess><SettingsPage /></RequireProAccess>} />
          <Route path="/pro/support" element={<RequireProAccess><ProSupportPage /></RequireProAccess>} />
          <Route path="/pro/support/conversations/:conversationId" element={<RequireProAccess><LegacySupportConversationRedirect pro /></RequireProAccess>} />
          <Route path="/pro/support/conversation/:conversationId" element={<RequireProAccess><ProSupportPage /></RequireProAccess>} />
          <Route path="/pro/support/ticket/:ticketId" element={<RequireProAccess><ProSupportPage /></RequireProAccess>} />

          {/* Public Report Card */}
          <Route path="/public/report-card/c/:slug" element={<LightModeWrapper><PublicReportCardPage /></LightModeWrapper>} />
          <Route path="/public/report-card/e/:slug" element={<LightModeWrapper><PublicReportCardPage /></LightModeWrapper>} />

          {/* Public Practitioner Directory */}
          <Route path="/directory" element={disabledRoute(PRACTITIONER_DIRECTORY_ENABLED, <LightModeWrapper><PractitionerDirectoryPage /></LightModeWrapper>)} />
          <Route path="/directory/:profileId" element={disabledRoute(PRACTITIONER_DIRECTORY_ENABLED, <LightModeWrapper><PractitionerDetailPage /></LightModeWrapper>)} />

          {/* Coach Spotlight Form (Requires Pro access) */}
          <Route path="/coach/spotlight-form" element={disabledRoute(PRACTITIONER_SPOTLIGHT_ENABLED, <RequireProAccess><SpotlightFormPage /></RequireProAccess>)} />

          {/* Coach Spotlight Form Demo (Public demo) */}
          <Route path="/coach/spotlight-demo" element={disabledRoute(PRACTITIONER_SPOTLIGHT_ENABLED, <LightModeWrapper><SpotlightFormPageDemo /></LightModeWrapper>)} />

          {/* Presentations series */}
          <Route path="/presentations" element={<LightModeWrapper><PresentationsIndexPage /></LightModeWrapper>} />
          <Route path="/presentations/:slug" element={<LightModeWrapper><PresentationDetailPage /></LightModeWrapper>} />
          <Route path="/tutorials" element={<LightModeWrapper><TutorialsIndexPage /></LightModeWrapper>} />
          <Route path="/tutorials/:platform/:slug" element={<LightModeWrapper><TutorialDetailPage /></LightModeWrapper>} />
          <Route path="/tutorials/:slug" element={<Navigate to="/tutorials" replace />} />

          {/* Public Scenario Pages (case-study landing pages) */}
          <Route path="/scenarios" element={<LightModeWrapper><ScenariosIndexPage /></LightModeWrapper>} />
          <Route path="/scenarios/:slug" element={<LightModeWrapper><ScenariosSlugRoute /></LightModeWrapper>} />
          <Route path="/scenarios/:category/:slug" element={<LightModeWrapper><ScenarioPage /></LightModeWrapper>} />

          {/* Coach Spotlight Page (Public) */}
          <Route path="/spotlight/:slug" element={disabledRoute(PRACTITIONER_SPOTLIGHT_ENABLED, <LightModeWrapper><SpotlightPage /></LightModeWrapper>)} />

          {/* Coach Spotlight Page Mockup (For Review) */}
          <Route path="/spotlight-mockup/:slug" element={disabledRoute(PRACTITIONER_SPOTLIGHT_ENABLED, <LightModeWrapper><SpotlightPageMockup /></LightModeWrapper>)} />
          <Route path="/spotlight-mockup" element={disabledRoute(PRACTITIONER_SPOTLIGHT_ENABLED, <LightModeWrapper><SpotlightPageMockup /></LightModeWrapper>)} />

          {/* Discovery entry from dashboard (requires login, generates token automatically) */}
          <Route path="/discoveries" element={<RequireAuth><DiscoveryEntryPage /></RequireAuth>} />

          {/* Discovery routes (token-based, no login required) */}
          <Route element={<TokenBasedRoute><DiscoveryLayout /></TokenBasedRoute>}>
            <Route path="/discovery" element={<DiscoveryFeedPage />} />
            <Route path="/discovery/respond" element={<DiscoveryResponsePage />} />
            <Route path="/discovery/results/:surveyId" element={<DiscoveryResultsPage />} />
            {/* Phase 2: Experiment outcome route deferred */}
            {/* <Route path="/discovery/experiment-result/:experimentId" element={<ExperimentOutcomePage />} /> */}
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      
      {/* View-as Banner - appears on all pages when in impersonation mode */}
      <ViewAsBanner />
      
      {/* Cookie Consent Banner - appears for users who haven't given consent */}
      <CookieConsentBanner />
      <MarketingGoogleAdsTracker />
      
      {/* Toast Notifications */}
      <Toaster />

      {/* First-session intent picker */}
      <IntentPickerController />
    </Router>
  )
}

export default App
